import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useSnackbar } from "../../utils/SnackbarProvider";

export default function ForgotPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1: username, 2: OTP, 3: reset
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [inputError, setInputError] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const { showSnackbar } = useSnackbar();
  /* =========================
     PREFILL FROM LOGIN
  ========================= */
  useEffect(() => {
    if (location.state?.username) {
      setUsername(location.state.username);
      fetchUser(location.state.username);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  let interval;

  if (step === 2 && timer > 0) {
    interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
  } else if (timer === 0) {
    setCanResend(true);
  }

  return () => clearInterval(interval);
}, [step, timer]);
  /* =========================
     FETCH USER DETAILS
  ========================= */
  const fetchUser = async (username) => {
    try {
      const res = await api.get(`/users/profile/${username}`);
      setPhone(res.data.user.mobile);
    } catch (err) {
      console.error(err);
    }
  };

  /* =========================
     SEND OTP
  ========================= */
  const sendOtp = async () => {
    if (!username.trim()) {
      showSnackbar("Enter email or mobile number", "error");
      return;
    }

    const isEmail = username.includes("@");
    let finalUsername = username.trim();

    if (isEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(finalUsername)) {
        showSnackbar("Enter a valid email address", "error");
        return;
      }
      finalUsername = finalUsername.toLowerCase();
    } else {
      // Reject if input contains non-digit chars
      const digitsOnly = finalUsername.replace(/^\+/, "");
      if (!/^\d+$/.test(digitsOnly)) {
        showSnackbar("Enter a valid mobile number", "error");
        return;
      }
      const cleaned = finalUsername.replace(/\D/g, "");
      // Reject if user typed country code (91 or 86)
      if (cleaned.startsWith("91") && cleaned.length > 10) {
        showSnackbar("Enter mobile number without country code", "error");
        return;
      }
      if (cleaned.startsWith("86") && cleaned.length > 11) {
        showSnackbar("Enter mobile number without country code", "error");
        return;
      }
      // Indian numbers: 10 digits starting with 6-9, Chinese: 11 digits starting with 1
      if (!((cleaned.length === 10 && /^[6-9]/.test(cleaned)) ||
            (cleaned.length === 11 && /^1/.test(cleaned)))) {
        showSnackbar("Enter a valid mobile number", "error");
        return;
      }
      finalUsername = `${countryCode}${cleaned}`;
    }

    try {
      const res = await api.post("/users/forgot-password/send-otp", {
        username: finalUsername,
      });

      setPhone(res.data.phone);
      setStep(2);

      // 🔥 RESET TIMER
      setTimer(30);
      setCanResend(false);

    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to send OTP", "error");
    }
  };

const resendOtp = async () => {
  try {
    await sendOtp();
  } catch (err) {
    showSnackbar("Failed to resend OTP", "error");
  }
};

  /* =========================
     VERIFY OTP
  ========================= */
  const verifyOtp = async () => {
    try {
      await api.post("/users/forgot-password/verify-otp", {
        username,
        otpCode: otp,
      });

      setStep(3);
    } catch (err) {
      showSnackbar("Invalid OTP", "error");
    }
  };

  /* =========================
     RESET PASSWORD
  ========================= */
  const resetPassword = async () => {
    try {
      await api.post("/users/forgot-password/reset", {
        username,
        password,
      });

      showSnackbar("Password updated successfully", "success");
      navigate("/login");
    } catch (err) {
      showSnackbar("Failed to reset password", "error");
    }
  };

  const maskPhone = (phone) => {
  if (!phone) return "";
  return phone.slice(0, 2) + "****" + phone.slice(-2);
};
  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f3f3f3",
        overflow: "hidden",
      }}
    >
      <Paper sx={{ p: 3, width: 400 }}>
        <Typography variant="h6" mb={2}>
          Forgot Password
        </Typography>

        {/* STEP 1 */}
        {step === 1 && (
          <>
            <TextField
              fullWidth
              label="Email or Mobile"
              value={username}
              onChange={(e) => {
                const val = e.target.value.toLowerCase();
                // Auto-detect country code from first digit
                if (val && !val.includes("@")) {
                  const firstDigit = val.replace(/\D/g, "")[0];
                  if (firstDigit === "1") setCountryCode("+86");
                  else if (/^[6-9]$/.test(firstDigit)) setCountryCode("+91");
                }
                setUsername(val);
                setInputError("");
              }}
              error={!!inputError}
              helperText={inputError}
              InputProps={{
                startAdornment: /^\+?\d+$/.test(username) ? (
                  <InputAdornment position="start">
                    <Select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      sx={{
                        fontSize: 14,
                        height: 32,
                        "& .MuiSelect-select": { py: 0, pr: 2 },
                        "& fieldset": { border: "none" },
                      }}
                    >
                      <MenuItem value="+91">+91</MenuItem>
                      <MenuItem value="+86">+86</MenuItem>
                    </Select>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ mb: 2 }}
            />
            <Button fullWidth variant="contained"
            sx={{
              backgroundColor: "#ff9900",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "20px",
              py: 1,
              "&:hover": { backgroundColor: "#ff8800" },
            }}
            onClick={sendOtp}>
              Send OTP
            </Button>
          </>
        )}

        {/* STEP 2 */}
        {step === 2 && (
  <>
    <Typography sx={{ mb: 1 }}>
      OTP sent to {maskPhone(phone)}
    </Typography>

    <TextField
      fullWidth
      label="Enter OTP"
      value={otp}
      onChange={(e) => setOtp(e.target.value)}
      sx={{ mb: 2 }}
    />

    <Button fullWidth variant="contained" 
    sx={{
              backgroundColor: "#ff9900",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "20px",
              py: 1,
              "&:hover": { backgroundColor: "#ff8800" },
            }}
    onClick={verifyOtp}>
      Verify OTP
    </Button>

    {/* ⏳ TIMER / RESEND */}
    <Typography
      sx={{
        mt: 2,
        fontSize: 13,
        color: canResend ? "#0066c0" : "gray",
        cursor: canResend ? "pointer" : "default",
        textAlign: "center",
      }}
      onClick={canResend ? resendOtp : undefined}
    >
      {canResend
        ? "Resend OTP"
        : `Resend OTP in ${timer}s`}
    </Typography>
  </>
)}

        {/* STEP 3 */}
        {step === 3 && (
          <>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Button fullWidth variant="contained" 
            sx={{
              backgroundColor: "#ff9900",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "20px",
              py: 1,
              "&:hover": { backgroundColor: "#ff8800" },
            }}
            onClick={resetPassword}>
              Reset Password
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}